import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GymClassificationEditor, gymValidationMessage } from '../components/gym/GymClassificationEditor';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Users,
  Dumbbell,
  Activity,
  Heart,
  Swords,
  RotateCcw,
  MoreHorizontal,
  Eye,
  ClipboardList,
  Wind,
  ShieldCheck,
  Trash2,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSport } from '../context/SportContext';

// Fallback static list (used until SportContext loads). The component
// switches to `sportSessionTypes` from useSport() at render time.
const SESSION_TYPES_FALLBACK = [
  { value: 'TRAINING', label: 'Training', icon: Activity },
  { value: 'TECHNICAL', label: 'Technical/Tactical', icon: Users },
  { value: 'GK_TRAINING', label: 'GK Training', icon: Activity },
  { value: 'MATCH', label: 'Match', icon: Swords },
  { value: 'GYM', label: 'Gym', icon: Dumbbell },
  { value: 'RECOVERY', label: 'Recovery', icon: Heart },
  { value: 'VBT', label: 'VBT', icon: Activity },
  { value: 'RTP', label: 'RTP', icon: RotateCcw },
  { value: 'OTHER', label: 'Other', icon: MoreHorizontal },
];

const PROGRAM_TYPES = [
  { value: 'TEAM', label: 'Team Program' },
  { value: 'RTP', label: 'RTP Program' },
];

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const getWeekDates = (baseDate) => {
  const date = new Date(baseDate);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Start on Monday
  const monday = new Date(date.setDate(diff));
  
  const week = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    week.push(d.toISOString().split('T')[0]);
  }
  return week;
};

const PlannerCalendarPage = () => {
  const { api, hasRole } = useAuth();
  const { t } = useLanguage();
  const { sessionTypes: sportSessionTypes, getSessionTypeMeta, resolveIcon } = useSport();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sport-aware session types — falls back to football defaults until loaded.
  const SESSION_TYPES = React.useMemo(() => {
    if (!sportSessionTypes || sportSessionTypes.length === 0) return SESSION_TYPES_FALLBACK;
    return sportSessionTypes.map(s => ({ ...s, icon: resolveIcon(s.icon) }));
  }, [sportSessionTypes, resolveIcon]);

  const getSessionTypeIcon = React.useCallback(
    (type) => resolveIcon(getSessionTypeMeta(type).icon),
    [getSessionTypeMeta, resolveIcon],
  );
  const getSessionTypeColor = React.useCallback(
    (type) => getSessionTypeMeta(type).color,
    [getSessionTypeMeta],
  );

  const [sessions, setSessions] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Week navigation - memoize weekDates to prevent re-renders
  const initialDate = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekDates(initialDate)[0]);
  const weekDates = React.useMemo(() => getWeekDates(currentWeekStart), [currentWeekStart]);
  
  // Filters
  const [teamFilter, setTeamFilter] = useState(searchParams.get('team') || 'all');
  const [programFilter, setProgramFilter] = useState(searchParams.get('program') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  
  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newSession, setNewSession] = useState({
    team_id: '',
    title: '',
    type: 'TRAINING',
    program_type: 'TEAM',
    description: '',
    duration_planned_min: 90,
    location: '',
    start_time: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState('team'); // 'team' or 'individual'

  // RTP structured content blocks — start EMPTY. The Physio/S&C must
  // explicitly enter every exercise, block and note. No template, no fallback,
  // no auto-population. See P0 RTP integrity hotfix.
  const [rtpBlocks, setRtpBlocks] = useState([]);
  const [rtpNotes, setRtpNotes] = useState('');

  // RTP exclusion prompt state
  const [rtpAthletes, setRtpAthletes] = useState([]);
  const [showRtpPrompt, setShowRtpPrompt] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  
  const canCreate = hasRole(['ADMIN', 'SNC', 'PHYSIO']);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [api]);

  const fetchAthletesForTeam = useCallback(async (teamId) => {
    if (!teamId) {
      setAthletes([]);
      return;
    }
    try {
      const response = await api.get(`/athletes?team_id=${teamId}`);
      setAthletes(response.data);
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
      setAthletes([]);
    }
  }, [api]);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('date_from', weekDates[0]);
      params.append('date_to', weekDates[6]);
      if (teamFilter !== 'all') params.append('team_id', teamFilter);
      if (programFilter !== 'all') params.append('program_type', programFilter);
      if (typeFilter !== 'all') params.append('session_type', typeFilter);
      
      const [sessRes, rxRes] = await Promise.all([
        api.get(`/sessions?${params.toString()}`),
        api.get('/prescriptions/by-date-range', {
          params: {
            date_from: weekDates[0],
            date_to: weekDates[6],
            ...(teamFilter !== 'all' ? { team_id: teamFilter } : {}),
          }
        }).catch(() => ({ data: [] })),
      ]);
      setSessions(sessRes.data);
      setPrescriptions(rxRes.data || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [api, weekDates, teamFilter, programFilter, typeFilter]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Fetch sessions when filters change
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  
  // Update URL params separately to avoid re-render loop
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('date', currentWeekStart);
    if (teamFilter !== 'all') params.set('team', teamFilter);
    if (programFilter !== 'all') params.set('program', programFilter);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    
    // Only update if params actually changed
    const newParamsStr = params.toString();
    const currentParamsStr = searchParams.toString();
    if (newParamsStr !== currentParamsStr) {
      setSearchParams(params, { replace: true });
    }
  }, [currentWeekStart, teamFilter, programFilter, typeFilter, searchParams, setSearchParams]);

  const getSessionsForDate = (date) => {
    return sessions.filter(s => s.date === date);
  };

  const getPrescriptionsForDate = (date) => {
    // Phase 3 canonical rule: Prescription is NOT a parallel object — the
    // canonical session already appears in the sessions list with a
    // "Prescription" source badge. Show a parallel Rx card ONLY when no
    // canonical session was materialised (e.g. draft template opened for
    // reference or legacy pre-Phase-1 rows).
    const canonicalRxIds = new Set(
      getSessionsForDate(date).filter(s => s.prescription_id).map(s => s.prescription_id)
    );
    return prescriptions.filter(p => {
      if (canonicalRxIds.has(p.id)) return false;
      if (p.prescription_type === 'MICROCYCLE') return false;
      if (p.prescription_type === 'SINGLE_SESSION' && p.date === date) return true;
      if (p.date === date) return true;
      return false;
    });
  };

  const RX_CAT_ICONS = { STRENGTH: Dumbbell, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart, MIXED: ClipboardList };
  const RX_CAT_COLORS = {
    STRENGTH: 'border-l-blue-500 bg-blue-500/10',
    AEROBIC: 'border-l-emerald-500 bg-emerald-500/10',
    RTP: 'border-l-amber-500 bg-amber-500/10',
    RECOVERY: 'border-l-purple-500 bg-purple-500/10',
    MIXED: 'border-l-zinc-500 bg-zinc-500/10',
  };

  const handlePrevWeek = () => {
    const prev = new Date(currentWeekStart);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeekStart(prev.toISOString().split('T')[0]);
  };

  const handleNextWeek = () => {
    const next = new Date(currentWeekStart);
    next.setDate(next.getDate() + 7);
    setCurrentWeekStart(next.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setCurrentWeekStart(getWeekDates(new Date().toISOString().split('T')[0])[0]);
  };

  const openCreateDialog = (date) => {
    setSelectedDate(date);
    setNewSession({
      team_id: teamFilter !== 'all' ? teamFilter : '',
      title: '',
      type: 'TRAINING',
      program_type: 'TEAM',
      description: '',
      duration_planned_min: 90,
      location: '',
      start_time: '',
    });
    setAssignmentMode('team');
    setSelectedAthletes([]);
    setRtpBlocks([]);
    setRtpNotes('');
    if (teamFilter !== 'all') {
      fetchAthletesForTeam(teamFilter);
    }
    setShowCreateDialog(true);
  };

  const buildSessionData = () => {
    const sessionData = {
      ...newSession,
      date: selectedDate,
      duration_planned_min: newSession.duration_planned_min === '' || newSession.duration_planned_min == null ? null : parseInt(newSession.duration_planned_min),
      gym_classification: newSession.type === 'GYM' ? newSession.gym_classification ?? null : null,
      start_time: newSession.start_time || null,
    };

    // For RTP sessions, build structured content_json from the block editor
    if (sessionData.type === 'RTP' || sessionData.program_type === 'RTP') {
      const cleanBlocks = rtpBlocks.map(block => {
        const b = { order: block.order, name: block.name, duration_min: block.duration_min || null };
        if (block.exercises && block.exercises.length > 0) {
          b.exercises = block.exercises.filter(ex => ex.name && ex.name.trim());
        }
        if (block.notes && block.notes.trim()) {
          b.notes = block.notes.trim();
        }
        return b;
      }).filter(b => b.name && b.name.trim());

      sessionData.content_json = {
        blocks: cleanBlocks,
        notes: rtpNotes || '',
      };
      const totalDuration = cleanBlocks.reduce((sum, b) => sum + (b.duration_min || 0), 0);
      if (totalDuration > 0 && !sessionData.duration_planned_min) {
        sessionData.duration_planned_min = totalDuration;
      }
    }

    // Pass target_athlete_ids when in individual mode
    if (assignmentMode === 'individual' && selectedAthletes.length > 0) {
      sessionData.target_athlete_ids = selectedAthletes;
    }

    return sessionData;
  };

  const submitSession = async (sessionData, excludeRtpAthleteIds = []) => {
    setIsCreating(true);
    try {
      // If excluding RTP athletes from a team session, convert to individual mode
      if (excludeRtpAthleteIds.length > 0 && !sessionData.target_athlete_ids) {
        const teamAthletesResp = await api.get(`/athletes?team_id=${sessionData.team_id}`);
        const allIds = teamAthletesResp.data.map(a => a.id);
        sessionData.target_athlete_ids = allIds.filter(id => !excludeRtpAthleteIds.includes(id));
      }

      // RTP sessions MUST have rtp_profile_id (backend enforces this)
      if (sessionData.type === 'RTP') {
        // RTP sessions must be assigned to specific athletes
        if (!sessionData.target_athlete_ids || sessionData.target_athlete_ids.length === 0) {
          toast.error('RTP sessions must be assigned to specific athletes');
          setIsCreating(false);
          return;
        }
        // Auto-link rtp_profile_id from the first assigned athlete's active profile
        if (!sessionData.rtp_profile_id && sessionData.target_athlete_ids.length >= 1) {
          try {
            const profilesResp = await api.get(`/rtp/profiles?athlete_id=${sessionData.target_athlete_ids[0]}`);
            const profiles = profilesResp.data || [];
            const activeProfile = profiles.find(p => p.status === 'ACTIVE') || profiles[0];
            if (activeProfile) {
              sessionData.rtp_profile_id = activeProfile.id;
            } else {
              toast.error('Selected athlete has no RTP profile. Create an RTP case first.');
              setIsCreating(false);
              return;
            }
          } catch (e) {
            toast.error('Could not verify athlete RTP profile');
            setIsCreating(false);
            return;
          }
        }
      }

      await api.post('/sessions', sessionData);
      toast.success(t('saveSuccess'));
      setShowCreateDialog(false);
      setShowRtpPrompt(false);
      setPendingSubmit(null);
      fetchSessions();
    } catch (err) {
      toast.error(gymValidationMessage(err, t('error')));
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    const sessionData = buildSessionData();

    // Issue Group 3: Check for RTP athletes in team-wide sessions
    if (assignmentMode === 'team' && sessionData.type !== 'RTP' && sessionData.team_id) {
      try {
        const rtpCheck = await api.get(`/athletes/team/${sessionData.team_id}/rtp-check`);
        if (rtpCheck.data.rtp_athletes && rtpCheck.data.rtp_athletes.length > 0) {
          setRtpAthletes(rtpCheck.data.rtp_athletes);
          setPendingSubmit(sessionData);
          setShowRtpPrompt(true);
          return;
        }
      } catch (e) { /* ignore if endpoint fails */ }
    }

    await submitSession(sessionData);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">{t('planner')}</h1>
          <p className="text-sm text-zinc-500 mt-1">Week view calendar</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleToday}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300"
            data-testid="today-button"
          >
            Today
          </Button>
          <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-sm">
            <Button
              onClick={handlePrevWeek}
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
              data-testid="prev-week"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-2 sm:px-3 text-xs sm:text-sm font-medium text-zinc-100 min-w-0 sm:min-w-[180px] text-center">
              {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
            </span>
            <Button
              onClick={handleNextWeek}
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
              data-testid="next-week"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="team-filter">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">{t('all')} {t('teams')}</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>{team.code} - {team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={programFilter} onValueChange={setProgramFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="program-filter">
              <SelectValue placeholder="All Programs" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Programs</SelectItem>
              {PROGRAM_TYPES.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Types</SelectItem>
              {SESSION_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[700px]">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-zinc-800">
          {weekDates.map((date) => {
            const d = new Date(date);
            const isToday = date === today;
            const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
            const dayNum = d.getDate();
            
            return (
              <div
                key={date}
                className={`p-3 text-center border-r border-zinc-800 last:border-r-0 ${
                  isToday ? 'bg-amber-500/5' : ''
                }`}
              >
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{dayName}</div>
                <div className={`text-lg font-semibold mt-1 ${
                  isToday ? 'text-amber-500' : 'text-zinc-100'
                }`}>
                  {dayNum}
                </div>
              </div>
            );
          })}
        </div>

        {/* Sessions Grid */}
        <div className="grid grid-cols-7 min-h-[400px]">
          {weekDates.map((date) => {
            const daySessions = getSessionsForDate(date);
            const isToday = date === today;
            
            return (
              <div
                key={date}
                className={`p-2 border-r border-zinc-800 last:border-r-0 min-h-[300px] ${
                  isToday ? 'bg-amber-500/5' : ''
                }`}
              >
                <div className="space-y-2">
                  {daySessions.map((session) => {
                    const Icon = getSessionTypeIcon(session.type);
                    const isRx = session.source === 'PRESCRIPTION' || !!session.prescription_id;
                    return (
                      <Link
                        key={session.id}
                        to={`/staff/planner/session/${session.id}`}
                        className={`block p-2 rounded-sm border transition-colors hover:bg-zinc-800/50 ${getSessionTypeColor(session.type)} ${isRx ? 'ring-1 ring-amber-500/50' : ''}`}
                        data-testid={`session-${session.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 mb-0.5">
                              {isRx && (
                                <span
                                  className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 uppercase font-semibold tracking-wide"
                                  data-testid={`source-badge-prescription-${session.id}`}
                                  title="Generated from a Prescription"
                                >
                                  <ClipboardList className="w-2.5 h-2.5" /> RX
                                </span>
                              )}
                              <div className="text-xs font-medium truncate">
                                {session.start_time ? <span className="text-amber-400/80">{session.start_time} · </span> : null}
                                {session.title}
                              </div>
                            </div>
                            <div className="text-[10px] opacity-70 mt-0.5">
                              {session.team_code} • {session.duration_planned_min ? `${session.duration_planned_min}m • ` : ''}{session.assignment_count || 0} <Users className="w-2.5 h-2.5 inline" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}

                  {/* Prescription cards */}
                  {getPrescriptionsForDate(date).map((rx) => {
                    const RxIcon = RX_CAT_ICONS[rx.category] || ClipboardList;
                    return (
                      <Link
                        key={`rx-${rx.id}`}
                        to={`/staff/prescriptions/${rx.id}`}
                        className={`block p-2 rounded-sm border-l-2 border border-zinc-800 transition-colors hover:bg-zinc-800/50 ${RX_CAT_COLORS[rx.category] || 'bg-zinc-800/20'}`}
                        data-testid={`planner-rx-${rx.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <RxIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{rx.title}</div>
                            <div className="text-[10px] opacity-70 mt-0.5">
                              <ClipboardList className="w-2.5 h-2.5 inline mr-0.5" />
                              {rx.category} • {rx.assignment_count || 0} <Users className="w-2.5 h-2.5 inline" />
                              {rx.completed_count > 0 && <span className="ml-1 text-emerald-400">{rx.completed_count} done</span>}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                  
                  {canCreate && (
                    <button
                      onClick={() => openCreateDialog(date)}
                      className="w-full p-2 text-xs text-zinc-500 hover:text-amber-500 hover:bg-zinc-800/30 rounded-sm border border-dashed border-zinc-700 hover:border-amber-500/50 transition-colors"
                      data-testid={`add-session-${date}`}
                    >
                      <Plus className="w-3 h-3 mx-auto" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Link
          to={`/staff/planner/day/${today}`}
          className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
        >
          View Today's Detail →
        </Link>
        <span className="text-zinc-700">|</span>
        <Link
          to={`/staff/planner/snapshot/${today}`}
          className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
        >
          Day Snapshot →
        </Link>
      </div>

      {/* Create Session Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Create Session</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {formatDate(selectedDate)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSession} className="flex flex-col overflow-hidden flex-1">
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('team')}</Label>
                  <Select
                    value={newSession.team_id}
                    onValueChange={(val) => {
                      setNewSession({ ...newSession, team_id: val });
                      fetchAthletesForTeam(val);
                      setSelectedAthletes([]);
                    }}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="session-team">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>{team.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Program</Label>
                  <Select
                    value={newSession.program_type}
                    onValueChange={(val) => setNewSession({ ...newSession, program_type: val })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="session-program">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {PROGRAM_TYPES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Title</Label>
                <Input
                  value={newSession.title}
                  onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                  placeholder="e.g., Morning Training"
                  className="bg-zinc-950 border-zinc-800"
                  required
                  data-testid="session-title"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Type</Label>
                  <Select
                    value={newSession.type}
                    onValueChange={(val) => {
                      const updates = { type: val };
                      if (val === 'RTP') {
                        updates.program_type = 'RTP';
                        setAssignmentMode('individual'); // RTP requires individual assignment
                      } else if (newSession.program_type === 'RTP') {
                        updates.program_type = 'TEAM';
                      }
                      setNewSession({ ...newSession, ...updates });
                    }}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="session-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {SESSION_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Duration (min)</Label>
                  <Input
                    type="number"
                    value={newSession.duration_planned_min}
                    onChange={(e) => setNewSession({ ...newSession, duration_planned_min: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="session-duration"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Start Time (optional)</Label>
                  <Input
                    type="time"
                    value={newSession.start_time}
                    onChange={(e) => setNewSession({ ...newSession, start_time: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="session-start-time"
                  />
                </div>
              </div>

              {newSession.type === 'GYM' && <GymClassificationEditor value={newSession.gym_classification ?? null}
                onChange={value => setNewSession({ ...newSession, gym_classification: value })}
                duration={newSession.duration_planned_min} prefix="planner-create-gym" />}
              <div className="space-y-2">
                <Label className="text-zinc-400">Location</Label>
                <Input
                  value={newSession.location}
                  onChange={(e) => setNewSession({ ...newSession, location: e.target.value })}
                  placeholder="e.g., Main Field"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="session-location"
                />
              </div>

              {/* Athlete Assignment */}
              <div className="space-y-3 pt-3 border-t border-zinc-800">
                <Label className="text-zinc-400">Athlete Assignment</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAssignmentMode('team')}
                    className={`flex-1 py-2 px-3 text-sm rounded-sm transition-colors ${
                      assignmentMode === 'team'
                        ? 'bg-amber-500 text-black font-medium'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Whole Team
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAssignmentMode('individual');
                      if (newSession.team_id) {
                        fetchAthletesForTeam(newSession.team_id);
                      }
                    }}
                    className={`flex-1 py-2 px-3 text-sm rounded-sm transition-colors ${
                      assignmentMode === 'individual'
                        ? 'bg-amber-500 text-black font-medium'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Specific Athletes
                  </button>
                </div>
                
                {assignmentMode === 'individual' && (
                  <div className="space-y-2">
                    {!newSession.team_id ? (
                      <p className="text-xs text-zinc-500">Select a team first to choose athletes</p>
                    ) : athletes.length === 0 ? (
                      <p className="text-xs text-zinc-500">Loading athletes...</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between text-xs text-zinc-500">
                          <span>{selectedAthletes.length} selected</span>
                          <button
                            type="button"
                            onClick={() => setSelectedAthletes(
                              selectedAthletes.length === athletes.length ? [] : athletes.map(a => a.id)
                            )}
                            className="text-amber-500 hover:text-amber-400"
                          >
                            {selectedAthletes.length === athletes.length ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1 bg-zinc-950 border border-zinc-800 rounded-sm p-2">
                          {athletes.map((athlete) => (
                            <label
                              key={athlete.id}
                              className="flex items-center gap-2 p-1.5 rounded hover:bg-zinc-800/50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedAthletes.includes(athlete.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAthletes([...selectedAthletes, athlete.id]);
                                  } else {
                                    setSelectedAthletes(selectedAthletes.filter(id => id !== athlete.id));
                                  }
                                }}
                                className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 focus:ring-amber-500"
                              />
                              <span className="text-sm text-zinc-300">
                                {athlete.first_name} {athlete.last_name}
                              </span>
                              <span className="text-xs text-zinc-500 ml-auto">{athlete.position}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Description</Label>
                <Textarea
                  value={newSession.description}
                  onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 min-h-[60px]"
                  data-testid="session-description"
                />
              </div>

              {/* RTP Structured Block Builder */}
              {(newSession.type === 'RTP' || newSession.program_type === 'RTP') && (
                <div className="space-y-3 pt-3 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-300 font-semibold">Session Blocks</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/50 h-7 text-xs"
                      onClick={() => {
                        setRtpBlocks([...rtpBlocks, {
                          order: rtpBlocks.length + 1,
                          name: '',
                          duration_min: 10,
                          exercises: [],
                          notes: '',
                        }]);
                      }}
                      data-testid="add-rtp-block"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Block
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                    {rtpBlocks.map((block, blockIdx) => (
                      <div key={blockIdx} className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                          <span className="text-[10px] text-zinc-500 font-mono w-5">{block.order}</span>
                          <Input
                            value={block.name}
                            onChange={(e) => {
                              const updated = [...rtpBlocks];
                              updated[blockIdx] = { ...updated[blockIdx], name: e.target.value };
                              setRtpBlocks(updated);
                            }}
                            placeholder="Block name (e.g. Warm-up)"
                            className="bg-zinc-900 border-zinc-700 h-7 text-sm flex-1"
                            data-testid={`rtp-block-name-${blockIdx}`}
                          />
                          <Input
                            type="number"
                            value={block.duration_min || ''}
                            onChange={(e) => {
                              const updated = [...rtpBlocks];
                              updated[blockIdx] = { ...updated[blockIdx], duration_min: parseInt(e.target.value) || null };
                              setRtpBlocks(updated);
                            }}
                            placeholder="min"
                            className="bg-zinc-900 border-zinc-700 h-7 text-sm w-16"
                            data-testid={`rtp-block-duration-${blockIdx}`}
                          />
                          <span className="text-[10px] text-zinc-500">min</span>
                          <button
                            type="button"
                            onClick={() => setRtpBlocks(rtpBlocks.filter((_, i) => i !== blockIdx).map((b, i) => ({ ...b, order: i + 1 })))}
                            className="p-1 text-zinc-600 hover:text-rose-400 transition-colors"
                            data-testid={`remove-rtp-block-${blockIdx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Exercises in this block */}
                        {block.exercises && block.exercises.length > 0 && (
                          <div className="pl-8 space-y-1">
                            {block.exercises.map((ex, exIdx) => (
                              <div key={exIdx} className="flex items-center gap-2">
                                <Input
                                  value={ex.name}
                                  onChange={(e) => {
                                    const updated = [...rtpBlocks];
                                    const exs = [...updated[blockIdx].exercises];
                                    exs[exIdx] = { ...exs[exIdx], name: e.target.value };
                                    updated[blockIdx] = { ...updated[blockIdx], exercises: exs };
                                    setRtpBlocks(updated);
                                  }}
                                  placeholder="Exercise name"
                                  className="bg-zinc-900 border-zinc-700 h-6 text-xs flex-1"
                                  data-testid={`rtp-ex-name-${blockIdx}-${exIdx}`}
                                />
                                <Input
                                  value={ex.sets || ''}
                                  onChange={(e) => {
                                    const updated = [...rtpBlocks];
                                    const exs = [...updated[blockIdx].exercises];
                                    exs[exIdx] = { ...exs[exIdx], sets: parseInt(e.target.value) || null };
                                    updated[blockIdx] = { ...updated[blockIdx], exercises: exs };
                                    setRtpBlocks(updated);
                                  }}
                                  placeholder="Sets"
                                  className="bg-zinc-900 border-zinc-700 h-6 text-xs w-14"
                                />
                                <span className="text-[10px] text-zinc-600">x</span>
                                <Input
                                  value={ex.reps || ''}
                                  onChange={(e) => {
                                    const updated = [...rtpBlocks];
                                    const exs = [...updated[blockIdx].exercises];
                                    exs[exIdx] = { ...exs[exIdx], reps: e.target.value };
                                    updated[blockIdx] = { ...updated[blockIdx], exercises: exs };
                                    setRtpBlocks(updated);
                                  }}
                                  placeholder="Reps"
                                  className="bg-zinc-900 border-zinc-700 h-6 text-xs w-16"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...rtpBlocks];
                                    const exs = updated[blockIdx].exercises.filter((_, i) => i !== exIdx);
                                    updated[blockIdx] = { ...updated[blockIdx], exercises: exs };
                                    setRtpBlocks(updated);
                                  }}
                                  className="p-0.5 text-zinc-600 hover:text-rose-400"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...rtpBlocks];
                            const exs = [...(updated[blockIdx].exercises || []), { name: '', sets: null, reps: '' }];
                            updated[blockIdx] = { ...updated[blockIdx], exercises: exs };
                            setRtpBlocks(updated);
                          }}
                          className="ml-8 text-[10px] text-zinc-500 hover:text-orange-400 transition-colors"
                          data-testid={`add-exercise-${blockIdx}`}
                        >
                          + Add exercise
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-xs">Session Notes</Label>
                    <Textarea
                      value={rtpNotes}
                      onChange={(e) => setRtpNotes(e.target.value)}
                      placeholder="e.g., No contact. Monitor for any pain."
                      className="bg-zinc-950 border-zinc-800 min-h-[40px] text-xs"
                      data-testid="rtp-session-notes"
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="pt-4 border-t border-zinc-800 flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !newSession.team_id || !newSession.title}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                data-testid="create-session-submit"
              >
                {isCreating ? t('loading') : t('create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* RTP Exclusion Prompt Dialog */}
      <Dialog open={showRtpPrompt} onOpenChange={(open) => { if (!open) { setShowRtpPrompt(false); setPendingSubmit(null); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <RotateCcw className="w-5 h-5" /> RTP Athletes Detected
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This team includes athletes currently in RTP. Do you want to include them in this assignment?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3">
            {rtpAthletes.map(a => (
              <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm">
                <RotateCcw className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                <span className="text-sm text-zinc-200">{a.first_name} {a.last_name}</span>
                <span className="text-xs text-zinc-500 ml-auto">{a.injury_type}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowRtpPrompt(false); setPendingSubmit(null); }}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="rtp-prompt-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (pendingSubmit) {
                  submitSession(pendingSubmit, rtpAthletes.map(a => a.id));
                }
              }}
              disabled={isCreating}
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              data-testid="rtp-prompt-exclude"
            >
              Exclude RTP Athletes
            </Button>
            <Button
              onClick={() => {
                if (pendingSubmit) {
                  submitSession(pendingSubmit);
                }
              }}
              disabled={isCreating}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
              data-testid="rtp-prompt-include"
            >
              Include All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlannerCalendarPage;
